import type { SVGProps } from 'react'
import type { LucideProps } from 'lucide-react'

const IconWrapper = ({
  children,
  size = 24,
  color,
  strokeWidth = 2.5,
  viewBox = '0 0 24 24',
  className = '',
  ...props
}: LucideProps & { viewBox?: string }) => {
  const isSelected = (color = 'currentColor')
  const isLargeIcon = viewBox.includes('120')
  const scaleFactor = isLargeIcon ? 3.0 : 1

  const finalStrokeWidth = Number(strokeWidth) * scaleFactor

  const finalStroke = isSelected ? 'currentColor' : color || '#171717'

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox={viewBox}
      fill="none"
      stroke={finalStroke}
      strokeWidth={finalStrokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={`lucide ${className}`}
      {...props}
    >
      {children}
    </svg>
  )
}

export const Starone = (props: LucideProps) => (
  <IconWrapper {...props} viewBox="-10 -10 120 125">
    <path
      d="M61.1807 17.1709L61.3984 17.4873L61.7607 17.3584L80.3857 10.7422L79.8369 30.4707L79.8262 30.8555L80.1953 30.9648L99.1582 36.5498L87.1084 52.1953L86.873 52.5L87.1084 52.8047L99.1582 68.4492L80.1953 74.0352L79.8262 74.1445L79.8369 74.5293L80.3857 94.2568L61.7607 87.6416L61.3984 87.5127L61.1807 87.8291L50 104.116L38.8193 87.8291L38.6016 87.5127L38.2393 87.6416L19.6133 94.2568L20.1631 74.5293L20.1738 74.1445L19.8047 74.0352L0.84082 68.4492L12.8916 52.8047L13.127 52.5L12.8916 52.1953L0.84082 36.5498L19.8047 30.9648L20.1738 30.8555L20.1631 30.4707L19.6133 10.7422L38.2393 17.3584L38.6016 17.4873L38.8193 17.1709L50 0.882812L61.1807 17.1709Z"
      fill="#F5F5F5"
    />
  </IconWrapper>
)

export const Startwo = (props: LucideProps) => (
  <IconWrapper {...props} viewBox="-10 -10 120 120">
    <path
      d="M57.2461 28.8818L57.4453 29.624L58.043 29.1416L79.7715 11.5859L69.7578 37.6465L69.4814 38.3652L70.25 38.3252L98.1631 36.873L74.7275 52.0811L74.0811 52.5L74.7275 52.9189L98.1631 68.126L70.25 66.6748L69.4814 66.6348L69.7578 67.3535L79.7715 93.4131L58.043 75.8584L57.4453 75.376L57.2461 76.1182L50 103.075L42.7539 76.1182L42.5547 75.376L41.957 75.8584L20.2275 93.4131L30.2422 67.3535L30.5186 66.6348L29.75 66.6748L1.83594 68.126L25.2725 52.9189L25.9189 52.5L25.2725 52.0811L1.83594 36.873L29.75 38.3252L30.5186 38.3652L30.2422 37.6465L20.2275 11.5859L41.957 29.1416L42.5547 29.624L42.7539 28.8818L50 1.92383L57.2461 28.8818Z"
      fill="#F5F5F5"
    />
  </IconWrapper>
)

export const Pentagon = (props: LucideProps) => (
  <IconWrapper {...props} viewBox="0 0 24 24">
    <path d="M12 2 L22 9.5 L18.2 21 L5.8 21 L2 9.5 Z" fill="none" />
  </IconWrapper>
)

/** Text Wrap "None" — text lines pass straight through the object, ignoring it. */
export const TextWrapNoneIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.333} {...props} viewBox="0 0 16 16">
    <path d="M2 8H14" />
    <path d="M2 13.332H14" />
    <path d="M2 2.66797H14" />
    <path d="M9.33594 5.33203H6.66927C5.93289 5.33203 5.33594 5.92898 5.33594 6.66536V9.33203C5.33594 10.0684 5.93289 10.6654 6.66927 10.6654H9.33594C10.0723 10.6654 10.6693 10.0684 10.6693 9.33203V6.66536C10.6693 5.92898 10.0723 5.33203 9.33594 5.33203Z" />
  </IconWrapper>
)

/** Text Wrap "Jump" — text lines that intersect the object are skipped, resuming below it. */
export const TextWrapJumpIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.333} {...props} viewBox="0 0 16 16">
    <path d="M2 13.332H14" />
    <path d="M2 2.66797H14" />
    <path d="M9.33594 5.33203H6.66927C5.93289 5.33203 5.33594 5.92898 5.33594 6.66536V9.33203C5.33594 10.0684 5.93289 10.6654 6.66927 10.6654H9.33594C10.0723 10.6654 10.6693 10.0684 10.6693 9.33203V6.66536C10.6693 5.92898 10.0723 5.33203 9.33594 5.33203Z" />
  </IconWrapper>
)

/** Text Wrap "Wrap" — text lines contour around the object's actual shape. */
export const TextWrapAroundIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.333} {...props} viewBox="0 0 16 16">
    <path d="M8.46179 5.62667C8.31507 5.34637 7.98828 5.24627 7.73486 5.41311C7.65402 5.46754 7.58764 5.54083 7.54145 5.62667L5.40733 9.77773C5.26061 10.0647 5.34731 10.4251 5.60073 10.5852C5.67944 10.6385 5.77251 10.6664 5.8675 10.6653H10.1357C10.4292 10.6653 10.6693 10.3984 10.6693 10.0714C10.6676 9.96916 10.6425 9.8687 10.5959 9.77773L8.46179 5.62667Z" />
    <path d="M12 8H14" />
    <path d="M2 8H4" />
    <path d="M2 13.332H14" />
    <path d="M2 2.66797H14" />
  </IconWrapper>
)

/** Text Wrap "Bounding box" — text lines wrap around the object's rectangular bounding box. */
export const TextWrapBoundingBoxIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.333} {...props} viewBox="0 0 16 16">
    <path d="M8.46179 5.62667C8.31507 5.34637 7.98828 5.24627 7.73486 5.41311C7.65402 5.46754 7.58764 5.54083 7.54145 5.62667L5.40733 9.77773C5.26061 10.0647 5.34731 10.4251 5.60073 10.5852C5.67944 10.6385 5.77251 10.6664 5.8675 10.6653H10.1357C10.4292 10.6653 10.6693 10.3984 10.6693 10.0714C10.6676 9.96916 10.6425 9.8687 10.5959 9.77773L8.46179 5.62667Z" />
    <path d="M12.6641 8H13.9974" />
    <path d="M2 8H3.33333" />
    <path d="M2 13.332H14" />
    <path d="M2 2.66797H14" />
  </IconWrapper>
)

export const Freeform = (props: LucideProps) => (
  <IconWrapper {...props} viewBox="0 0 100 100">
    <path
      d="M39.1123 4.54199C43.0891 1.96961 46.7505 0.26146 50.2607 0.526367V0.527344C53.7806 0.802805 57.2653 3.06747 61.0596 6.07812C64.8103 9.05421 68.8486 12.7469 73.3525 15.6318C77.8515 18.52 82.8664 20.6236 87.209 22.7471C91.5932 24.8909 95.2712 27.0461 97.3203 30.042H97.3213C99.3674 33.0288 99.8508 36.9282 99.2744 41.0898C98.6985 45.2471 97.0714 49.6111 94.9707 53.459H94.9697C92.8809 57.3036 90.3188 60.6274 87.6279 63.9707C85.1111 67.0977 82.4759 70.2484 80.0527 73.8301L79.5713 74.5527C78.2995 76.4902 77.0937 78.5565 75.8926 80.626C74.6893 82.6992 73.4901 84.7758 72.2217 86.7549C69.6815 90.7182 66.8949 94.2359 63.332 96.4756V96.4766C56.2833 100.914 46.0863 100.446 39.71 95.2148L39.4102 94.9619C36.2419 92.2262 34.0513 88.2384 31.9326 83.9414C29.83 79.6769 27.7971 75.1027 24.9502 71.3682C22.0965 67.6247 18.432 64.7344 14.8809 61.9268C11.3115 59.1046 7.86074 56.3689 5.31055 52.9014C0.217705 45.9295 -1.25563 36.074 2.96582 29.4307L2.9668 29.4297C5.07116 26.0934 8.63242 23.5074 12.8584 21.0264C14.9676 19.7881 17.2215 18.5876 19.5254 17.333C21.8247 16.0809 24.1685 14.7779 26.4365 13.3447L26.4375 13.3438C28.6998 11.9061 30.8823 10.343 32.9893 8.82324C35.1006 7.3003 37.1322 5.82284 39.1123 4.54199Z"
      fill="#D9D9D9"
      stroke="black"
    />
  </IconWrapper>
)

/**
 * The rounded-square body of a 24x24 selection badge tile (inset 3, radius 4). Paired with
 * `SELECTION_BADGE_TILE_FRAME_PATH`. Exported because the text-link ports draw the same tile
 * inside an SVG group rather than as a standalone icon (WMPCM-9199) — keeping one set of paths is
 * what makes a port and an overflow badge drawn at the same coordinate line up exactly.
 */
export const SELECTION_BADGE_TILE_INNER_PATH =
  'M3 7C3 4.79086 4.79086 3 7 3H17C19.2091 3 21 4.79086 21 7V17C21 19.2091 19.2091 21 17 21H7C4.79086 21 3 19.2091 3 17V7Z'

/** The 1px translucent-white ring around a badge tile (between radius 5 and radius 4). */
export const SELECTION_BADGE_TILE_FRAME_PATH =
  'M2 17V7C2 4.23858 4.23858 2 7 2V3C4.79086 3 3 4.79086 3 7V17C3 19.14 4.68056 20.8879 6.79395 20.9951L7 21H17C19.2091 21 21 19.2091 21 17V7C21 4.79086 19.2091 3 17 3V2C19.7614 2 22 4.23858 22 7V17C22 19.7614 19.7614 22 17 22H7C4.23858 22 2 19.7614 2 17ZM17 2V3H7V2H17Z'

export type SelectionBadgeIconProps = {
  size?: number
} & Omit<SVGProps<SVGSVGElement>, 'width' | 'height' | 'viewBox'>

export type SelectionLockIconProps = SelectionBadgeIconProps
export type SelectionFreezeIconProps = SelectionBadgeIconProps

function SelectionBadgeTileChrome() {
  return (
    <>
      <path d={SELECTION_BADGE_TILE_INNER_PATH} fill="#F5F5F5" />
      <path d={SELECTION_BADGE_TILE_FRAME_PATH} fill="white" fillOpacity="0.8" />
    </>
  )
}

/** Lock-on-tile graphic for locked selection chrome (editor). */
export function SelectionLockIcon({ size = 24, className, ...props }: SelectionLockIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <SelectionBadgeTileChrome />
      <path
        d="M8.25 11.25V8.25C8.25 7.25544 8.64509 6.30161 9.34835 5.59835C10.0516 4.89509 11.0054 4.5 12 4.5C12.9946 4.5 13.9484 4.89509 14.6517 5.59835C15.3549 6.30161 15.75 7.25544 15.75 8.25V11.25M6.75 11.25H17.25C18.0784 11.25 18.75 11.9216 18.75 12.75V18C18.75 18.8284 18.0784 19.5 17.25 19.5H6.75C5.92157 19.5 5.25 18.8284 5.25 18V12.75C5.25 11.9216 5.92157 11.25 6.75 11.25Z"
        stroke="var(--color-page-editor-lock)"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Freeze-on-tile graphic for frozen selection chrome (editor). */
export function SelectionFreezeIcon({
  size = 24,
  className,
  clipPathId,
  ...props
}: SelectionFreezeIconProps & { clipPathId: string }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <SelectionBadgeTileChrome />
      <g clipPath={`url(#${clipPathId})`}>
        <path
          d="M4.5 12H19.5M12 4.5V19.5M18 15L15 12L18 9M6 9L9 12L6 15M15 6L12 9L9 6M9 18L12 15L15 18"
          stroke="var(--color-page-editor-freeze)"
          strokeWidth="1.333"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </g>
      <defs>
        <clipPath id={clipPathId}>
          <rect width="18" height="18" fill="white" transform="translate(3 3)" />
        </clipPath>
      </defs>
    </svg>
  )
}

export type SelectionOverflowIconProps = SelectionBadgeIconProps

/** Overflow-on-tile graphic shown on text boxes whose content exceeds their bounds (editor). */
export function SelectionOverflowIcon({
  size = 24,
  className,
  ...props
}: SelectionOverflowIconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      {...props}
    >
      <SelectionBadgeTileChrome />
      <path
        d="M12 6V18M6 12H18"
        stroke="var(--color-page-editor-flow-text, #dc2626)"
        strokeWidth="1.333"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  )
}

/** Mirror Move icon (Layout panel) — custom design, not a lucide icon. */
export const MirrorMoveIcon = ({ className, ...props }: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M10.1159 4.77279C10.4256 4.57373 10.838 4.663 11.0371 4.97266L11.8939 6.30599C12.0351 6.5256 12.0351 6.80774 11.8939 7.02734L11.0371 8.36068C10.838 8.67033 10.4256 8.75961 10.1159 8.56055C9.80617 8.36145 9.71626 7.94904 9.91536 7.63932L10.112 7.33333H7.33333C6.96514 7.33333 6.66667 7.03486 6.66667 6.66667C6.66667 6.29848 6.96514 6 7.33333 6H10.112L9.91536 5.69401C9.71626 5.3843 9.80617 4.97189 10.1159 4.77279Z"
      fill="currentColor"
    />
    <path
      d="M4.66667 5.33333C5.03486 5.33333 5.33333 5.63181 5.33333 6V7.33333C5.33333 7.70152 5.03486 8 4.66667 8H3.33333C2.96514 8 2.66667 7.70152 2.66667 7.33333V6C2.66667 5.63181 2.96514 5.33333 3.33333 5.33333H4.66667Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.66667 0C5.55072 0 6.39832 0.351441 7.02344 0.976563C7.13321 1.08633 7.23743 1.19706 7.33333 1.31185C7.42924 1.19706 7.53346 1.08633 7.64323 0.976563C8.26835 0.351441 9.11595 0 10 0H14C14.3682 0 14.6667 0.298477 14.6667 0.666667V12C14.6667 12.3682 14.3682 12.6667 14 12.6667H9.33333C8.97971 12.6667 8.64067 12.8072 8.39063 13.0573C8.14058 13.3073 8 13.6464 8 14C8 14.3682 7.70152 14.6667 7.33333 14.6667C6.96514 14.6667 6.66667 14.3682 6.66667 14C6.66667 13.6464 6.52609 13.3073 6.27604 13.0573C6.02599 12.8072 5.68696 12.6667 5.33333 12.6667H0.666667C0.298477 12.6667 0 12.3682 0 12V0.666667C0 0.298477 0.298477 0 0.666667 0H4.66667ZM1.33333 11.3333H5.33333C5.80562 11.3333 6.26465 11.4592 6.66667 11.6914V10.7702C6.66683 10.4021 6.96525 10.1035 7.33333 10.1035C7.70142 10.1035 7.99983 10.4021 8 10.7702V11.6914C8.40202 11.4592 8.86104 11.3333 9.33333 11.3333H13.3333V1.33333H10C9.46957 1.33333 8.96101 1.5442 8.58594 1.91927C8.15571 2.3495 8.00007 2.63849 8 3.10352C8 3.47171 7.70152 3.77018 7.33333 3.77018C6.96514 3.77018 6.66667 3.47171 6.66667 3.10352C6.6666 2.63849 6.51096 2.3495 6.08073 1.91927C5.70566 1.5442 5.1971 1.33333 4.66667 1.33333H1.33333V11.3333Z"
      fill="currentColor"
    />
  </svg>
)

/** Mirror Copy icon (Layout panel) — custom design, not a lucide icon. */
export const MirrorCopyIcon = ({ className, ...props }: LucideProps) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="15"
    height="15"
    viewBox="0 0 15 15"
    fill="none"
    className={className}
    {...props}
  >
    <path
      d="M4.66667 5.33333C5.03486 5.33333 5.33333 5.63181 5.33333 6V7.33333C5.33333 7.70152 5.03486 8 4.66667 8H3.33333C2.96514 8 2.66667 7.70152 2.66667 7.33333V6C2.66667 5.63181 2.96514 5.33333 3.33333 5.33333L4.66667 5.33333Z"
      fill="currentColor"
    />
    <path
      d="M11.3333 5.33333C11.7015 5.33333 12 5.63181 12 6L12 7.33333C12 7.70152 11.7015 8 11.3333 8H10C9.63181 8 9.33333 7.70152 9.33333 7.33333L9.33333 6C9.33333 5.63181 9.63181 5.33333 10 5.33333L11.3333 5.33333Z"
      fill="currentColor"
    />
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M4.66667 0C5.55072 0 6.39832 0.351441 7.02344 0.976563C7.13575 1.08888 7.23915 1.20837 7.33333 1.33398C7.42751 1.20837 7.53091 1.08888 7.64323 0.976563C8.26835 0.351441 9.11595 0 10 0L14 0C14.3682 0 14.6667 0.298477 14.6667 0.666667L14.6667 12C14.6667 12.3682 14.3682 12.6667 14 12.6667H9.33333C8.97971 12.6667 8.64067 12.8072 8.39063 13.0573C8.14058 13.3073 8 13.6464 8 14C8 14.3682 7.70152 14.6667 7.33333 14.6667C6.96514 14.6667 6.66667 14.3682 6.66667 14C6.66667 13.6464 6.52609 13.3073 6.27604 13.0573C6.02599 12.8072 5.68696 12.6667 5.33333 12.6667H0.666667C0.298477 12.6667 0 12.3682 0 12L0 0.666667C0 0.298477 0.298477 0 0.666667 0L4.66667 0ZM1.33333 11.3333H5.33333C5.80562 11.3333 6.26465 11.4592 6.66667 11.6914L6.66667 3.33333C6.66667 2.8029 6.4558 2.29434 6.08073 1.91927C5.70566 1.5442 5.1971 1.33333 4.66667 1.33333L1.33333 1.33333L1.33333 11.3333ZM10 1.33333C9.46957 1.33333 8.96101 1.5442 8.58594 1.91927C8.21087 2.29434 8 2.8029 8 3.33333L8 11.6914C8.40202 11.4592 8.86104 11.3333 9.33333 11.3333H13.3333L13.3333 1.33333L10 1.33333Z"
      fill="currentColor"
    />
  </svg>
)

/** Drop Cap style "Inset" (wire `dropCap: 'INS'`) — the cap sits flush inside the column's left edge. */
export const DropCapInsetIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.33} {...props} viewBox="0 0 16 16">
    <path d="M10 3.66666H14M10 8.33331H14M2 13H14M2 8.33331L4.36867 3.18398C4.39638 3.12868 4.43893 3.08219 4.49156 3.0497C4.54419 3.01721 4.60482 3 4.66667 3C4.72852 3 4.78915 3.01721 4.84177 3.0497C4.8944 3.08219 4.93695 3.12868 4.96467 3.18398L7.33333 8.33331M2.61328 6.99997H6.71995" />
  </IconWrapper>
)

/** Drop Cap style "Outset" (wire `dropCap: 'OUT'`) — the cap hangs left of the column into the margin. */
export const DropCapOutsetIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.33} {...props} viewBox="0 0 16 16">
    <path d="M10 3.66666H14M10 8.33331H14M10 13H14M2 8.33331L4.36867 3.18398C4.39638 3.12868 4.43893 3.08219 4.49156 3.0497C4.54419 3.01721 4.60482 3 4.66667 3C4.72852 3 4.78915 3.01721 4.84177 3.0497C4.8944 3.08219 4.93695 3.12868 4.96467 3.18398L7.33333 8.33331M2.61328 6.99997H6.71995" />
  </IconWrapper>
)

/** Text decoration "Underline" toggle icon (Type panel → Decoration). */
export const UnderlineIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.33} {...props} viewBox="0 0 16 16">
    <path d="M4.00033 2.66675V6.66675C4.00033 7.72761 4.42175 8.74503 5.1719 9.49518C5.92204 10.2453 6.93946 10.6667 8.00033 10.6667C9.06119 10.6667 10.0786 10.2453 10.8288 9.49518C11.5789 8.74503 12.0003 7.72761 12.0003 6.66675V2.66675M2.66699 13.3334H13.3337" />
  </IconWrapper>
)

/** Spell Check — left-panel rail tool icon. */
export const SpellCheckIcon = (props: LucideProps) => (
  <IconWrapper strokeWidth={1.33} {...props} viewBox="0 0 16 16">
    <path d="M4 10.6667L8 2.66675L12 10.6667M5.33333 8.00008H10.6667M10.6667 13.3334L12 14.6667L14.6667 12.0001" />
  </IconWrapper>
)

/** Filled circle info icon — used in "Object kept within pasteboard" toast. */
export function InfoFilledIcon({ size = 20, className, ...props }: SelectionBadgeIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={size}
      height={size}
      viewBox="0 0 16 16"
      fill="none"
      className={className}
      {...props}
    >
      <path
        fillRule="evenodd"
        clipRule="evenodd"
        d="M16 8C16 10.1217 15.1571 12.1566 13.6569 13.6569C12.1566 15.1571 10.1217 16 8 16C5.87827 16 3.84344 15.1571 2.34315 13.6569C0.842855 12.1566 0 10.1217 0 8C0 5.87827 0.842855 3.84344 2.34315 2.34315C3.84344 0.842855 5.87827 0 8 0C10.1217 0 12.1566 0.842855 13.6569 2.34315C15.1571 3.84344 16 5.87827 16 8ZM9 4C9 4.26522 8.89464 4.51957 8.70711 4.70711C8.51957 4.89464 8.26522 5 8 5C7.73478 5 7.48043 4.89464 7.29289 4.70711C7.10536 4.51957 7 4.26522 7 4C7 3.73478 7.10536 3.48043 7.29289 3.29289C7.48043 3.10536 7.73478 3 8 3C8.26522 3 8.51957 3.10536 8.70711 3.29289C8.89464 3.48043 9 3.73478 9 4ZM7 7C6.80109 7 6.61032 7.07902 6.46967 7.21967C6.32902 7.36032 6.25 7.55109 6.25 7.75C6.25 7.94891 6.32902 8.13968 6.46967 8.28033C6.61032 8.42098 6.80109 8.5 7 8.5H7.253C7.29041 8.50001 7.32734 8.50842 7.36106 8.52461C7.39479 8.54079 7.42445 8.56434 7.44787 8.59351C7.47128 8.62269 7.48785 8.65674 7.49636 8.69317C7.50486 8.7296 7.50508 8.76747 7.497 8.804L7.038 10.87C6.98108 11.1259 6.98237 11.3913 7.04179 11.6466C7.10121 11.902 7.21723 12.1407 7.38129 12.3452C7.54535 12.5496 7.75325 12.7146 7.98963 12.828C8.22602 12.9413 8.48485 13.0001 8.747 13H9C9.19891 13 9.38968 12.921 9.53033 12.7803C9.67098 12.6397 9.75 12.4489 9.75 12.25C9.75 12.0511 9.67098 11.8603 9.53033 11.7197C9.38968 11.579 9.19891 11.5 9 11.5H8.747C8.70959 11.5 8.67266 11.4916 8.63894 11.4754C8.60521 11.4592 8.57555 11.4357 8.55213 11.4065C8.52872 11.3773 8.51215 11.3433 8.50364 11.3068C8.49514 11.2704 8.49492 11.2325 8.503 11.196L8.962 9.13C9.01892 8.8741 9.01763 8.60868 8.95821 8.35335C8.89879 8.09802 8.78277 7.8593 8.61871 7.65483C8.45465 7.45036 8.24675 7.28536 8.01037 7.17201C7.77398 7.05867 7.51515 6.99989 7.253 7H7Z"
        fill="currentColor"
      />
    </svg>
  )
}
